import api from './api';

// ============================================
// Plan Types
// ============================================

export type PlanTier = 'free' | 'starter' | 'professional' | 'enterprise' | 'custom';
export type BillingInterval = 'monthly' | 'annual' | 'custom';

export interface PlanFeatures {
  // 1. Bot Studio (Build Layer)
  bots: {
    visual_bot_builder: boolean;
    flow_templates: boolean;
    multi_bot: boolean;
    bot_versioning: boolean;
    conversation_handoff: boolean;
  };
  // 2. AI & Knowledge Engine (Intelligence Layer)
  knowledge: {
    rag_knowledge_base: boolean;
    vertex_ai_search: boolean;
    intent_detection: boolean;
    entity_extraction: boolean;
    language_detection: boolean;
    llm_conversations: boolean;
    tools: boolean;
    mcp_servers: boolean;
    translation: boolean;
    intelligence_engines: boolean;
  };
  // 3. Omnichannel & Voice (Experience Layer)
  channels: {
    web_chat: boolean;
    sms: boolean;
    whatsapp: boolean;
    facebook_messenger: boolean;
    slack: boolean;
    teams: boolean;
    voice: boolean;
  };
  // 4. Customer Data Platform (CRM & Data Layer)
  crm_data: {
    contacts: boolean;
    lifecycle_stages: boolean;
    segments: boolean;
    crm_sync: boolean;
    slate_crm: boolean;
    banner_sis: boolean;
  };
  // 5. Engagement & Outreach (Orchestration Layer)
  outreach: {
    broadcast_campaigns: boolean;
    whatsapp_templates: boolean;
    ambassadors: boolean;
    programs: boolean;
    notification_triggers: boolean;
    notification_analytics: boolean;
    yield_forecasts: boolean;
    consent_management: boolean;
    messaging_policy: boolean;
  };
  // 6. Engagement Hub (Operations Layer)
  engagement_hub: {
    queues: boolean;
    schedules: boolean;
    wallboard: boolean;
    script_evolution: boolean;
    emotional_resolution: boolean;
    root_causes: boolean;
  };
  // 7. Voice Platform (Voice Infrastructure Layer)
  voice: {
    telephony: boolean;
    recordings: boolean;
    call_tracking: boolean;
    voice_ai_stt: boolean;
    voice_ai_tts: boolean;
  };
  // 8. Integrations (Ecosystem Layer)
  integrations: {
    ccaas_genesys: boolean;
    ccaas_8x8: boolean;
    ccaas_amazon_connect: boolean;
    ccaas_google: boolean;
    dialogflow: boolean;
    custom_webhooks: boolean;
    api_access: boolean;
  };
  // 9. Analytics & Insights (Data Intelligence Layer)
  analytics: {
    dashboard: boolean;
    custom_reports: boolean;
    scheduled_reports: boolean;
    export_data: boolean;
    sentiment_analysis: boolean;
    audit_logs: boolean;
  };
  // 10. Security & Compliance (Trust Layer)
  security: {
    sso: boolean;
    mfa: boolean;
    ip_allowlist: boolean;
    content_safety: boolean;
    compliance: boolean;
  };
  // 11. Platform & Enterprise (Foundation Layer)
  platform: {
    custom_branding: boolean;
    white_label: boolean;
    dedicated_support: boolean;
    sla_guarantee: boolean;
  };
  // 12. Cloud Fax (OmniFax Layer)
  fax?: {
    cloud_fax?: boolean;
    batch_fax?: boolean;
    scheduled_fax?: boolean;
    cover_page_templates?: boolean;
    inbound_routing?: boolean;
    fax_to_email?: boolean;
    api_access?: boolean;
  };
}

export interface PlanLimits {
  conversations_per_month: number;
  messages_per_month: number;
  llm_tokens_per_month: number;
  rag_queries_per_month: number;
  embeddings_per_month: number;
  stt_minutes_per_month: number;
  tts_minutes_per_month: number;
  storage_gb: number;
  documents_total: number;
  bots: number;
  knowledge_bases: number;
  users: number;
  integrations: number;
  custom_reports: number;
  api_calls_per_day: number;
  webhook_endpoints: number;
  webhook_calls_per_day: number;
  fax_pages_per_month: number;
  fax_numbers: number;
}

export interface Plan {
  id: string;
  name: string;
  code: string;
  tier: PlanTier;
  description: string | null;
  is_active: boolean;
  is_public: boolean;
  is_system: boolean;
  partner_id: string | null;
  tenant_id: string | null;
  price_monthly_cents: number;
  price_annual_cents: number;
  currency: string;
  trial_days: number;
  features: Partial<PlanFeatures>;
  limits: Partial<PlanLimits>;
  overage_pricing: Record<string, number>;
  display_order: number;
  metadata: Record<string, any>;
  created_at: string;
  updated_at: string;
}

export interface PlanCreateRequest {
  name: string;
  code: string;
  tier: PlanTier;
  description?: string;
  is_public?: boolean;
  partner_id?: string;
  price_monthly_cents: number;
  price_annual_cents: number;
  currency?: string;
  trial_days?: number;
  features: Partial<PlanFeatures>;
  limits: Partial<PlanLimits>;
  overage_pricing?: Record<string, number>;
  display_order?: number;
}

export interface PlanUpdateRequest {
  name?: string;
  description?: string;
  is_active?: boolean;
  is_public?: boolean;
  price_monthly_cents?: number;
  price_annual_cents?: number;
  trial_days?: number;
  features?: Partial<PlanFeatures>;
  limits?: Partial<PlanLimits>;
  overage_pricing?: Record<string, number>;
  display_order?: number;
}

// ============================================
// Subscription Types
// ============================================

export interface Subscription {
  id: string;
  tenant_id: string;
  plan_type: string;
  features: Partial<PlanFeatures>;
  usage_limits: Partial<PlanLimits>;
  billing_cycle: BillingInterval;
  starts_at: string;
  ends_at: string | null;
  is_active: boolean;
  created_at: string;
  // Joined data
  tenant_name?: string;
  partner_name?: string;
  plan_name?: string;
}

export interface SubscriptionCreateRequest {
  tenant_id: string;
  plan_code: string;
  billing_cycle?: BillingInterval;
  start_trial?: boolean;
}

export interface SubscriptionUpdateRequest {
  plan_code?: string;
  billing_cycle?: string;
  features_override?: Partial<PlanFeatures>;
  limits_override?: Partial<PlanLimits>;
}

export interface UsageSummary {
  tenant_id: string;
  plan: string;
  billing_cycle: string;
  period_start: string;
  period_end: string;
  usage: Record<string, {
    used: number;
    limit: number;
    remaining: number;
    is_unlimited: boolean;
    percent_used: number;
  }>;
}

export interface SubscriptionResult {
  success: boolean;
  subscription_id?: string;
  message?: string;
  action?: string;
  previous_plan?: string;
  new_plan?: string;
  proration_amount_cents?: number;
}

// ============================================
// Invoice Types
// ============================================

export interface InvoiceLineItem {
  description: string;
  quantity: number;
  unit_price: number;
  total: number;
}

export interface Invoice {
  id: string;
  invoice_number: string;
  tenant_id: string;
  period_start: string;
  period_end: string;
  line_items: InvoiceLineItem[];
  subtotal: number;
  tax: number;
  total: number;
  status: 'draft' | 'sent' | 'paid' | 'overdue' | 'cancelled';
  due_date: string;
  paid_at?: string;
  created_at: string;
}

export interface UsageHistory {
  period: string;
  period_start: string;
  period_end: string;
  conversations: number;
  messages: number;
  llm_tokens: number;
  rag_queries: number;
  total_cost: number;
}

export interface UsageBreakdown {
  period: string;
  period_start: string;
  period_end: string;
  breakdown: {
    conversations: { count: number; cost: number };
    messages: { count: number; cost: number };
    llm: { tokens: number; cost: number };
    rag: { queries: number; cost: number };
    voice: { stt_minutes: number; tts_minutes: number; cost: number };
  };
  total_cost: number;
  plan: string | null;
}

export interface InvoiceListParams {
  status?: string;
  limit?: number;
  offset?: number;
}

export interface InvoiceCreateRequest {
  tenant_id: string;
  period_start: string;
  period_end: string;
  line_items: InvoiceLineItem[];
  notes?: string;
}

// ============================================
// Platform Admin Types
// ============================================

export interface PlatformRevenue {
  total_revenue: number;
  total_revenue_cents: number;
  paid_invoices_count: number;
  outstanding: number;
  outstanding_cents: number;
  outstanding_count: number;
  overdue: number;
  overdue_cents: number;
  overdue_count: number;
  period_start?: string;
  period_end?: string;
}

export interface PartnerRevenue {
  partner_id: string;
  partner_name: string;
  revenue: number;
  revenue_cents: number;
  tenant_count: number;
  percentage: number;
}

export interface SubscriptionsSummary {
  total_active: number;
  total_inactive: number;
  by_plan: Record<string, number>;
}

export const billingService = {
  // ============================================
  // Plan Management
  // ============================================

  /**
   * List all plans
   */
  async listPlans(params: { include_inactive?: boolean; tier?: PlanTier } = {}): Promise<Plan[]> {
    const response = await api.get('/api/v1/billing/plans', { params });
    return response.data;
  },

  /**
   * Get a specific plan
   */
  async getPlan(planId: string): Promise<Plan> {
    const response = await api.get(`/api/v1/billing/plans/${planId}`);
    return response.data;
  },

  /**
   * Get plan by code
   */
  async getPlanByCode(code: string): Promise<Plan> {
    const response = await api.get(`/api/v1/billing/plans/code/${code}`);
    return response.data;
  },

  /**
   * Create a new plan
   */
  async createPlan(data: PlanCreateRequest): Promise<Plan> {
    const response = await api.post('/api/v1/billing/plans', data);
    return response.data;
  },

  /**
   * Update a plan
   */
  async updatePlan(planId: string, data: PlanUpdateRequest): Promise<Plan> {
    const response = await api.patch(`/api/v1/billing/plans/${planId}`, data);
    return response.data;
  },

  /**
   * Delete a plan (soft delete - sets is_active to false)
   */
  async deletePlan(planId: string): Promise<{ message: string }> {
    const response = await api.delete(`/api/v1/billing/plans/${planId}`);
    return response.data;
  },

  /**
   * Clone a plan to create a new one
   */
  async clonePlan(planId: string, newCode: string, newName: string): Promise<Plan> {
    const response = await api.post(`/api/v1/billing/plans/${planId}/clone`, {
      new_code: newCode,
      new_name: newName,
    });
    return response.data;
  },

  // ============================================
  // Subscription Management
  // ============================================

  /**
   * List all subscriptions
   */
  async listSubscriptions(params: {
    is_active?: boolean;
    plan_type?: string;
    partner_id?: string;
    limit?: number;
    offset?: number;
  } = {}): Promise<Subscription[]> {
    const response = await api.get('/api/v1/billing/subscriptions', { params });
    return response.data;
  },

  /**
   * Get current tenant's subscription (uses auth context)
   */
  async getCurrentSubscription(): Promise<Subscription> {
    const response = await api.get('/api/v1/billing/subscriptions/current');
    return response.data;
  },

  /**
   * Get subscription by ID
   */
  async getSubscription(subscriptionId: string): Promise<Subscription> {
    const response = await api.get(`/api/v1/billing/subscriptions/${subscriptionId}`);
    return response.data;
  },

  /**
   * Get subscription for a specific tenant
   */
  async getTenantSubscription(tenantId: string): Promise<Subscription> {
    const response = await api.get(`/api/v1/billing/subscriptions/tenant/${tenantId}`);
    return response.data;
  },

  /**
   * Create a new subscription
   */
  async createSubscription(data: SubscriptionCreateRequest): Promise<SubscriptionResult> {
    const response = await api.post('/api/v1/billing/subscriptions', data);
    return response.data;
  },

  /**
   * Change subscription plan
   */
  async changePlan(tenantId: string, newPlanCode: string, immediate: boolean = true): Promise<SubscriptionResult> {
    const response = await api.post(`/api/v1/billing/subscriptions/tenant/${tenantId}/change-plan`, {
      new_plan_code: newPlanCode,
      immediate,
    });
    return response.data;
  },

  /**
   * Cancel subscription
   */
  async cancelSubscription(tenantId: string, immediate: boolean = false): Promise<SubscriptionResult> {
    const response = await api.post(`/api/v1/billing/subscriptions/tenant/${tenantId}/cancel`, {
      immediate,
    });
    return response.data;
  },

  /**
   * Reactivate subscription
   */
  async reactivateSubscription(tenantId: string, planCode?: string): Promise<SubscriptionResult> {
    const response = await api.post(`/api/v1/billing/subscriptions/tenant/${tenantId}/reactivate`, {
      plan_code: planCode,
    });
    return response.data;
  },

  /**
   * Update subscription features (custom overrides)
   */
  async updateSubscriptionFeatures(
    tenantId: string,
    features: Partial<PlanFeatures>,
    merge: boolean = true
  ): Promise<SubscriptionResult> {
    const response = await api.patch(`/api/v1/billing/subscriptions/tenant/${tenantId}/features`, {
      features_override: features,
      merge,
    });
    return response.data;
  },

  /**
   * Update subscription limits (custom overrides)
   */
  async updateSubscriptionLimits(
    tenantId: string,
    limits: Partial<PlanLimits>,
    merge: boolean = true
  ): Promise<SubscriptionResult> {
    const response = await api.patch(`/api/v1/billing/subscriptions/tenant/${tenantId}/limits`, {
      limits_override: limits,
      merge,
    });
    return response.data;
  },

  /**
   * Get usage summary for a tenant
   */
  async getSubscriptionUsage(tenantId: string): Promise<UsageSummary> {
    const response = await api.get(`/api/v1/billing/subscriptions/tenant/${tenantId}/usage`);
    return response.data;
  },

  // ============================================
  // Invoice Management
  // ============================================

  /**
   * List invoices with optional filtering
   */
  async listInvoices(params: InvoiceListParams = {}): Promise<Invoice[]> {
    const response = await api.get('/api/v1/billing/invoices', { params });
    return response.data;
  },

  /**
   * Get a specific invoice
   */
  async getInvoice(invoiceId: string): Promise<Invoice> {
    const response = await api.get(`/api/v1/billing/invoices/${invoiceId}`);
    return response.data;
  },

  /**
   * Create a new invoice
   */
  async createInvoice(data: InvoiceCreateRequest): Promise<Invoice> {
    const response = await api.post('/api/v1/billing/invoices', data);
    return response.data;
  },

  /**
   * Send an invoice
   */
  async sendInvoice(invoiceId: string): Promise<{ message: string; invoice_id: string }> {
    const response = await api.post(`/api/v1/billing/invoices/${invoiceId}/send`);
    return response.data;
  },

  /**
   * Mark invoice as paid
   */
  async markInvoicePaid(invoiceId: string, paymentReference?: string): Promise<{ message: string; invoice_id: string }> {
    const response = await api.post(`/api/v1/billing/invoices/${invoiceId}/mark-paid`, null, {
      params: { payment_reference: paymentReference },
    });
    return response.data;
  },

  /**
   * Download invoice as PDF/HTML
   */
  async downloadInvoice(invoiceId: string): Promise<Blob> {
    const response = await api.get(`/api/v1/billing/invoices/${invoiceId}/download`, {
      responseType: 'blob',
    });
    return response.data;
  },

  /**
   * Get outstanding invoices summary
   */
  async getOutstandingSummary(): Promise<{
    outstanding_count: number;
    outstanding_total: number;
    overdue_count: number;
    overdue_total: number;
  }> {
    const response = await api.get('/api/v1/billing/invoices/summary/outstanding');
    return response.data;
  },

  /**
   * Get current billing period usage
   */
  async getCurrentUsage(): Promise<UsageSummary> {
    const response = await api.get('/api/v1/billing/usage/current');
    return response.data;
  },

  /**
   * Get usage history
   */
  async getUsageHistory(months: number = 6): Promise<{ history: UsageHistory[] }> {
    const response = await api.get('/api/v1/billing/usage/history', { params: { months } });
    return response.data;
  },

  /**
   * Get usage breakdown by category
   */
  async getUsageBreakdown(period: 'current' | 'last' = 'current'): Promise<UsageBreakdown> {
    const response = await api.get('/api/v1/billing/usage/breakdown', { params: { period } });
    return response.data;
  },

  /**
   * Get daily usage data
   */
  async getDailyUsage(dateFrom?: string, dateTo?: string): Promise<{
    daily_usage: Array<{
      date: string;
      conversations: number;
      messages: number;
      llm_tokens: number;
      rag_queries: number;
    }>;
  }> {
    const response = await api.get('/api/v1/billing/usage/daily', {
      params: { date_from: dateFrom, date_to: dateTo },
    });
    return response.data;
  },

  // ============================================
  // Platform Admin Methods (Super-Admin)
  // ============================================

  /**
   * Get platform-wide revenue summary
   */
  async getPlatformRevenue(params: { period_start?: string; period_end?: string } = {}): Promise<PlatformRevenue> {
    const response = await api.get('/api/v1/billing/platform/revenue', { params });
    return response.data;
  },

  /**
   * Get revenue breakdown by partner
   */
  async getRevenueByPartner(params: { period_start?: string; period_end?: string; limit?: number } = {}): Promise<{
    partners: PartnerRevenue[];
    total_revenue: number;
    period_start?: string;
    period_end?: string;
  }> {
    const response = await api.get('/api/v1/billing/platform/revenue/by-partner', { params });
    return response.data;
  },

  /**
   * List platform-wide invoices (super-admin)
   */
  async listPlatformInvoices(params: {
    partner_id?: string;
    status?: string;
    limit?: number;
    offset?: number;
  } = {}): Promise<Invoice[]> {
    const response = await api.get('/api/v1/billing/platform/invoices', { params });
    return response.data;
  },

  /**
   * Get subscriptions summary
   */
  async getSubscriptionsSummary(): Promise<SubscriptionsSummary> {
    const response = await api.get('/api/v1/billing/platform/subscriptions/summary');
    return response.data;
  },

  /**
   * Get platform usage summary
   */
  async getPlatformUsageSummary(params: { period_start?: string; period_end?: string } = {}): Promise<{
    period_start: string;
    period_end: string;
    usage: Record<string, { total: number; tenant_count: number }>;
  }> {
    const response = await api.get('/api/v1/billing/platform/usage/summary', { params });
    return response.data;
  },

  /**
   * Generate batch invoices for all tenants
   */
  async generateBatchInvoices(params: {
    period_start: string;
    period_end: string;
    partner_id?: string;
  }): Promise<{
    total_tenants: number;
    success_count: number;
    failure_count: number;
    results: Array<{
      tenant_id: string;
      tenant_name: string;
      success: boolean;
      invoice_number?: string;
      message?: string;
    }>;
  }> {
    const response = await api.post('/api/v1/billing/platform/invoices/generate-batch', null, { params });
    return response.data;
  },
};

export default billingService;
